import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Loader, Stack } from '@lyearn/ui';
import { Dialog } from '@/components/Modals';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { RelatedEntityInput } from '@/types/schema';

import useFile from '../../../hooks/useFile';
import useFileActions from '../../../hooks/useFileActions';
import { DialogProps } from '../types';
import {
  adaptFormValuesToCreateProjectInput,
  adaptFormValuesToUpdateProjectInput,
  ProjectEditorFormValues,
} from './components/Editor/config';
import ProjectEditorContainer from './ProjectEditorContainer';

export const dialogSize = css`
  max-width: 52rem;
  height: 74.5rem;
`;

function ProjectEditor({
  onClose,
  fileId,
  clientId,
  entity,
  entityId,
  shouldNavigateOnSuccess = true,
}: DialogProps) {
  const { t } = useTranslation('VisibilitySetting');
  const [formikSubmit, setFormikSubmit] = useState<any>();
  const { state, methods } = useFileActions({ onSuccess: onClose });
  const { isFetching } = state;
  const { createProject, updateProject } = methods;
  const { id: currentUserId } = useUserContext();

  const { fetching: fetchingFile, file, error } = useFile({ fileId });
  const onSubmit = useCallback(
    (val: ProjectEditorFormValues) => {
      if (fileId) {
        const adaptedValues = adaptFormValuesToUpdateProjectInput(val);
        updateProject(fileId, adaptedValues);
        return;
      }

      const adaptedValues = adaptFormValuesToCreateProjectInput(val);
      createProject(
        {
          ...adaptedValues,
          ...(entityId && entity
            ? { entity: { entityId, entityType: entity } as RelatedEntityInput }
            : {}),
        },
        shouldNavigateOnSuccess,
      );
    },
    [fileId, createProject, entity, entityId, updateProject, shouldNavigateOnSuccess],
  );

  const initialValues = useMemo(() => {
    return {
      projectOwnerId: currentUserId,
      clientIds: clientId ? [clientId] : undefined,
    };
  }, [clientId, currentUserId]);

  return (
    <Dialog
      open
      action={{
        disabled: isFetching || fetchingFile,
        isSubmitting: isFetching,
        text: file ? t('Common:Actions.Save') : t('Common:Actions.Create'),
        onSubmit: formikSubmit,
        buttonSize: 'large',
      }}
      className="h-full"
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      closeButton={{
        size: 'large',
        className: 'end-12 top-20',
      }}
      header={{
        title: file ? t('Task:EditProject') : t('Task:NewProject'),
        description: file ? t('Task:EditProjectDescription') : t('Task:ProjectDescription'),
        variants: {
          description: 'body-short-01',
        },
        className: 'pt-28 pb-24',
      }}
      onClose={onClose}>
      <Stack className="h-full flex-1 border-b border-divider" direction="col">
        <Suspense fallback={<Loader className="mx-auto my-auto" />}>
          <ProjectEditorContainer
            error={error}
            fetching={fetchingFile}
            file={file}
            initialValues={initialValues}
            setFormikSubmit={setFormikSubmit}
            onSubmit={onSubmit}
          />
        </Suspense>
      </Stack>
    </Dialog>
  );
}

export default ProjectEditor;
